import GavelIcon from '@mui/icons-material/Gavel';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
    Box,
    Chip,
    Container,
    Fab,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import LawAppBar from './LawAppBar';
import './Pricing.css';

const planFeatures = [
    {
        feature: 'Monthly Price',
        basic: '$95',
        professional: '$175',
        enterprise: 'Custom'
    },
    {
        feature: 'Included Capacity',
        basic: '1,000 credits',
        professional: '3,000 credits',
        enterprise: 'Custom scalable capacity'
    },
    {
        feature: 'Additional Usage',
        basic: '$50 for 500 credits; prorated',
        professional: '$50 for 1,000 credits; prorated',
        enterprise: 'Volume pricing'
    },
    {
        feature: 'Best For',
        basic: 'Occasional use',
        professional: 'Solo attorneys, small and medium PI firms',
        enterprise: 'Large firms & high-volume medical reviewers'
    }
];

export default function Pricing() {
    const [cookies] = useCookies(['given_name', 'userid', 'picture', 'email', 'credential']);
    const navigate = useNavigate();

    return (
        <div className="App">
            <LawAppBar cookies={cookies} />
            <Container
                maxWidth="lg"
                sx={{
                    padding: { xs: '20px', sm: '40px' },
                    marginTop: '40px',
                    marginBottom: '60px'
                }}
            >
                {/* Hero Section */}
                <Box sx={{ textAlign: 'center', marginBottom: '60px' }}>
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 400,
                            fontSize: { xs: '2rem', sm: '2.8rem', md: '3.2rem' },
                            marginBottom: '1rem',
                            letterSpacing: '-0.02em',
                            lineHeight: 1.2
                        }}
                    >
                        Transparent Pricing. No Contracts. No Friction.
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: { xs: '1rem', sm: '1.2rem' },
                            fontWeight: 400,
                            maxWidth: 800,
                            margin: '0 auto',
                            lineHeight: 1.6
                        }}
                    >
                        Stop losing hours to manual medical record reviews. Start for free.
                    </Typography>
                </Box>

                {/* Select Your Plan intro */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '16px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Select Your Plan
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            maxWidth: 720,
                            margin: '0 auto'
                        }}
                    >
                        Choose the capacity that fits your firm's monthly case volume. All plans are month-to-month—cancel or change tiers at any time.
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: '0.95rem',
                            marginTop: '16px',
                            fontStyle: 'italic'
                        }}
                    >
                        We provide 3 usage tiers depending on usage volume:
                    </Typography>
                </Paper>

                {/* Plan Cards */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                        gap: 3,
                        textAlign: 'left',
                        marginBottom: '48px'
                    }}
                >
                    {/* Basic */}
                    <Paper
                        elevation={0}
                        sx={{
                            padding: { xs: '28px', sm: '32px' },
                            borderRadius: '8px',
                            boxShadow: 'var(--shadow-light)',
                            border: '1px solid var(--border-color)',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        }}
                    >
                        <Typography
                            variant="h5"
                            component="h3"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: '1.35rem',
                                marginBottom: '12px'
                            }}
                        >
                            Basic
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                marginBottom: '8px'
                            }}
                        >
                            $95
                            <Typography component="span" variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 400 }}>
                                {' '}
                                per month
                            </Typography>
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'var(--text-primary)', marginBottom: '4px', fontWeight: 500 }}
                        >
                            1000 credits included
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '0.875rem' }}
                        >
                            Sufficient for 1-3 cases
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--text-primary)', marginBottom: '16px' }}>
                            $1 for each additional 10 credits
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: 'auto' }}>
                            Suitable for: Occasional use; longer term trial
                        </Typography>
                    </Paper>

                    {/* Professional - Most Popular */}
                    <Paper
                        elevation={0}
                        sx={{
                            padding: { xs: '28px', sm: '32px' },
                            borderRadius: '8px',
                            boxShadow: 'var(--shadow-light)',
                            border: '2px solid var(--accent-color)',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            position: 'relative'
                        }}
                    >
                        <Chip
                            icon={<StarIcon sx={{ fontSize: '1rem !important' }} />}
                            label="Most popular plan"
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: -10,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: 'var(--button-gradient)',
                                color: '#1a1a1a',
                                fontWeight: 600,
                                '& .MuiChip-icon': { color: '#1a1a1a' }
                            }}
                        />
                        <Typography
                            variant="h5"
                            component="h3"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: '1.35rem',
                                marginBottom: '12px',
                                marginTop: '8px'
                            }}
                        >
                            Professional
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                marginBottom: '8px'
                            }}
                        >
                            $175
                            <Typography component="span" variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 400 }}>
                                {' '}
                                per month
                            </Typography>
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'var(--text-primary)', marginBottom: '4px', fontWeight: 500 }}
                        >
                            3000 credits included
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '0.875rem' }}
                        >
                            Sufficient for 3-10 cases
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--text-primary)', marginBottom: '16px' }}>
                            $1 for each additional 20 credits
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: 'auto' }}>
                            Suitable for: Active solo attorneys, small, medium PI firms, and medical reviewers
                        </Typography>
                    </Paper>

                    {/* Enterprise */}
                    <Paper
                        elevation={0}
                        sx={{
                            padding: { xs: '28px', sm: '32px' },
                            borderRadius: '8px',
                            boxShadow: 'var(--shadow-light)',
                            border: '1px solid var(--border-color)',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        }}
                    >
                        <Typography
                            variant="h5"
                            component="h3"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: '1.35rem',
                                marginBottom: '12px'
                            }}
                        >
                            Enterprise
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                marginBottom: '8px'
                            }}
                        >
                            Custom pricing
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'var(--text-secondary)', marginBottom: '12px' }}
                        >
                            Pricing is based on customizations such as:
                        </Typography>
                        <Box component="ul" sx={{ paddingLeft: '20px', margin: '0 0 16px 0', flex: 1 }}>
                            <Typography component="li" variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.7, marginBottom: '6px' }}>
                                Custom integrations, workflows, and APIs
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.7, marginBottom: '6px' }}>
                                Custom capacity management with optimizations for high volume usage
                            </Typography>
                            <Typography component="li" variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.7, marginBottom: '6px' }}>
                                Advanced permissions and user management
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                            Suitable for: Large firms & high-volume medical reviewers
                        </Typography>
                    </Paper>
                </Box>

                {/* Feature Comparison Table */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '24px', sm: '32px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px',
                        overflow: 'hidden',
                        textAlign: 'left'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Plan Comparison
                    </Typography>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 320 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600, borderColor: 'var(--border-color)' }}>
                                        Feature
                                    </TableCell>
                                    <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600, borderColor: 'var(--border-color)' }}>
                                        Basic
                                    </TableCell>
                                    <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600, borderColor: 'var(--border-color)' }}>
                                        Professional
                                    </TableCell>
                                    <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600, borderColor: 'var(--border-color)' }}>
                                        Enterprise
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {planFeatures.map((row) => (
                                    <TableRow key={row.feature}>
                                        <TableCell sx={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                                            {row.feature}
                                        </TableCell>
                                        <TableCell sx={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                                            {row.basic}
                                        </TableCell>
                                        <TableCell sx={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                                            {row.professional}
                                        </TableCell>
                                        <TableCell sx={{ color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
                                            {row.enterprise}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* The AttorneyAide Advantage */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px',
                        textAlign: 'left'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                        <VerifiedIcon
                            sx={{
                                fontSize: { xs: '2rem', sm: '2.5rem' },
                                color: 'var(--accent-color)'
                            }}
                        />
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                fontSize: { xs: '1.8rem', sm: '2.2rem' },
                                letterSpacing: '-0.01em',
                                margin: 0
                            }}
                        >
                            The AttorneyAide Advantage
                        </Typography>
                    </Box>
                    <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: '1.2rem',
                            marginBottom: '12px'
                        }}
                    >
                        Built for Precision (AccuAide™)
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Every plan is backed by our AccuAide Integrity Engine which proactively finds and flags issues for your professional review. All information is linked to the original document, allowing for one-click human verification.
                    </Typography>
                </Paper>

                {/* FAQs */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px',
                        textAlign: 'left'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        FAQs
                    </Typography>

                    <Box sx={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }} component="h3" variant="subtitle1">
                            How does the Free Trial work?
                        </Typography>
                        <Typography sx={{ color: 'var(--text-primary)', lineHeight: 1.8 }}>
                            Sign up and get immediate access to the platform. Most firms immediately see the value of our platform by processing a single medical record. No payment information, setup, or training is necessary.
                        </Typography>
                    </Box>
                    <Box sx={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }} component="h3" variant="subtitle1">
                            How do Credits Work?
                        </Typography>
                        <Typography sx={{ color: 'var(--text-primary)', lineHeight: 1.8 }}>
                            We use a flexible credit system to ensure you only pay for the processing you actually need. A credit is roughly sufficient to process 10 pages of typed text. Your credit balance resets at the start of each billing cycle.
                        </Typography>
                    </Box>
                    <Box sx={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }} component="h3" variant="subtitle1">
                            What happens if I run out of included credits?
                        </Typography>
                        <Typography sx={{ color: 'var(--text-primary)', lineHeight: 1.8 }}>
                            Most users find that the included credits are sufficient. High volume users can buy more credits. You will be informed when you hit 80% of your allocated credits so that you are not surprised.
                        </Typography>
                    </Box>
                    <Box sx={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }} component="h3" variant="subtitle1">
                            Do I have to sign a long-term contract?
                        </Typography>
                        <Typography sx={{ color: 'var(--text-primary)', lineHeight: 1.8 }}>
                            No. All plans are billed monthly. We believe our value should keep you here, not a legal contract.
                        </Typography>
                    </Box>
                    <Box sx={{ paddingBottom: '0', marginBottom: '0' }}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }} component="h3" variant="subtitle1">
                            What if I have massive case loads with 100K pages of medical records?
                        </Typography>
                        <Typography sx={{ color: 'var(--text-primary)', lineHeight: 1.8 }}>
                            Our platform is designed to deal with large files. We can set up custom optimizations for high-volume litigation without affecting throughput or latency. If you process large workloads, then <Link
                                href="/contact"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontWeight: 600,
                                    textDecoration: 'underline',
                                    '&:hover': { color: 'var(--text-secondary)', textDecoration: 'underline', opacity: 0.9 }
                                }}
                            >
                                let us know
                            </Link> so that we can provide the best experience.
                        </Typography>
                    </Box>
                </Paper>

                {/* Need Help CTA */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '16px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Need Help Choosing a Plan?
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '24px'
                        }}
                    >
                        If you're unsure which plan fits your workflow or have questions, we're happy to help.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                        <Link
                            href="/contact"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                '&:hover': { color: 'var(--text-secondary)', textDecoration: 'underline', opacity: 0.9 }
                            }}
                        >
                            Contact Us
                        </Link>
                        <Typography component="span" sx={{ color: 'var(--text-secondary)' }}>
                            or
                        </Typography>
                        <Link
                            href="https://calendar.app.google/LuoqX3Xk43KMWquv5"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                '&:hover': { color: 'var(--text_-secondary)', textDecoration: 'underline', opacity: 0.9 }
                            }}
                        >
                            Schedule a quick 15-minute call
                        </Link>
                    </Box>
                </Paper>

                {/* Call to Action */}
                <Box
                    sx={{
                        textAlign: 'center',
                        padding: { xs: '40px 20px', sm: '60px 40px' },
                        marginTop: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 400,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '32px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Ready to reclaim your time?
                    </Typography>
                    <Fab
                        variant="extended"
                        size="large"
                        color="primary"
                        onClick={() => navigate('/mycases')}
                        sx={{
                            background: 'var(--button-gradient)',
                            boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '1.05rem',
                            padding: '15px 28px',
                            borderRadius: '16px',
                            minHeight: '56px',
                            height: '56px',
                            lineHeight: '1.5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: 'auto',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            border: 'none',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 30px rgba(212, 175, 55, 0.6)',
                                background: 'var(--button-gradient-hover)'
                            },
                            '&:active': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)'
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                transition: 'left 0.6s'
                            },
                            '&:hover::before': {
                                left: '100%'
                            }
                        }}
                    >
                        <GavelIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                        Start Your Free Trial Now
                    </Fab>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'var(--text-secondary)',
                            marginTop: '16px',
                            fontSize: '0.95rem'
                        }}
                    >
                        No credit card required. No setup fees.
                    </Typography>
                </Box>
            </Container>
            <Footer />
        </div>
    );
}

